import Box from "@mui/material/Box";

/**
 * Kernel, powered by Lopie One — the logo, as something you can render.
 *
 * Until now the only brand assets in this repo existed to feed Cognito's
 * branding designer: two SVGs and four base64 blobs under
 * `infrastructure/`, colour baked in, one pair per colour scheme. A micro-app
 * author had nothing. Every app that wanted a logo was going to draw its own,
 * and five slightly different logos is a worse failure than five slightly
 * different buttons for the same reason five slightly different refusals is.
 *
 * ## Why components and not files
 *
 * `currentColor`. An `<img src="kernel-mark.svg">` is a picture of a teal
 * wordmark, and a picture of a teal wordmark on `#0e1416` is a teal wordmark
 * you cannot read. Inlining the paths means the mark takes the colour of
 * whatever it sits in, so one asset is correct in light, dark, and the
 * default "system" state where neither is stamped — which is the state the
 * theme's own notes say is the one people forget.
 *
 * It also means no network request, no flash of nothing, and no second file
 * to lose track of.
 *
 * The shapes here are the shapes in `src/brand/*.svg`, which stay as the
 * source of record and as what regenerates Cognito's copies. Keeping the
 * paths in two places is a real cost and the smaller one: the alternative is
 * an SVG loader in every consumer's bundler config, and `remote-auth`'s whole
 * argument is that this package does not impose a build.
 */

const MARK_PATHS = [
  "M44 8h4v10.5L57 8h5l-9 10.6L62.5 32h-5l-7.2-10.2L48 24.6V32h-4z",
  "M66 32V8h15v3.6H70v6.3h10.2v3.6H70v6.9h11.3V32z",
  "M86 32V8h9.4c4.9 0 7.9 2.6 7.9 6.9 0 3.2-1.7 5.4-4.6 6.3L104 32h-4.7l-4.7-9.9H90V32zm4-13.4h5c2.6 0 4.2-1.3 4.2-3.6S97.6 11.5 95 11.5h-5z",
  "M108 32V8h4.3l11 16.6V8h4v24h-4.2l-11.1-16.7V32z",
  "M132 32V8h15v3.6h-11v6.3h10.2v3.6H136v6.9h11.3V32z",
  "M152 32V8h4v20.4h11V32z",
];

/**
 * The square, on its own. For a favicon-sized surface, an app-bar at 390px,
 * or anywhere the word would not fit.
 *
 * `size` is one number because it is square, and forcing a caller to pass two
 * equal numbers is an invitation to pass two different ones.
 */
export function KernelIcon({ size = 24, title, sx, ...rest }) {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 40 40"
      width={size}
      height={size}
      // Decorative by default and labelled when it is not. A logo beside the
      // word "Kernel" read aloud twice is noise; a logo that is the only
      // thing in a link is a link with no name.
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : "true"}
      sx={{ color: "primary.main", display: "block", flexShrink: 0, ...sx }}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g fill="none" stroke="currentColor" strokeWidth="3">
        <rect x="4" y="4" width="32" height="32" rx="10" />
      </g>
      <circle cx="20" cy="20" r="7.5" fill="currentColor" />
    </Box>
  );
}

/**
 * The wordmark: the square, then KERNEL as outlines.
 *
 * Outlines rather than live text on purpose — a system-font wordmark is a
 * different shape on every machine, and a webfont is a network request paid
 * on every cold load of a tool people open twenty times a day. The theme
 * makes the same argument about body text and lands the other way, because
 * body text being the reader's own font is a feature and a logo being the
 * reader's own font is not a logo.
 *
 * `height` rather than `width`: it sits on a baseline next to text, and text
 * is measured in heights.
 */
export function KernelMark({ height = 28, title = "Kernel", sx, ...rest }) {
  return (
    <Box
      component="svg"
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 -2 172 44"
      height={height}
      width={(height * 172) / 44}
      role={title ? "img" : "presentation"}
      aria-hidden={title ? undefined : "true"}
      sx={{ color: "primary.main", display: "block", flexShrink: 0, ...sx }}
      {...rest}
    >
      {title ? <title>{title}</title> : null}
      <g fill="none" stroke="currentColor" strokeWidth="2.5">
        <rect x="5" y="7" width="26" height="26" rx="8" />
      </g>
      <circle cx="18" cy="20" r="6" fill="currentColor" />
      <g fill="currentColor">
        {MARK_PATHS.map((d) => (
          <path key={d} d={d} />
        ))}
      </g>
    </Box>
  );
}

/**
 * The lockup: the mark, and the attribution under it.
 *
 * *Kernel* is the product a customer governs their data through and is what
 * appears in the interface. *Lopie One* is the platform underneath and
 * appears as attribution, not as the headline — same reasoning as the LO-31
 * rename: one word, one meaning. This component exists so that an app author
 * does not have to remember which of the two names is the big one.
 *
 * `subtitle` replaces the attribution for a surface that needs to say
 * something else; passing `null` removes it, which is right for an app bar
 * at 390px and wrong almost everywhere else.
 */
export function KernelLockup({ height = 24, subtitle, sx, ...rest }) {
  const caption = subtitle === undefined ? "powered by Lopie One" : subtitle;
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, ...sx }} {...rest}>
      <KernelMark height={height} />
      {caption ? (
        <Box
          component="span"
          sx={{
            color: "text.secondary",
            fontSize: "0.6875rem",
            letterSpacing: "0.04em",
            // Aligned to the K, not to the square, so the two lines read as
            // one block rather than as a logo with a caption drifting left.
            // The K starts at x=44 in a 44-tall viewBox, so at any rendered
            // height the offset is exactly that height in pixels.
            pl: `${height}px`,
          }}
        >
          {caption}
        </Box>
      ) : null}
    </Box>
  );
}
